import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronDown } from "lucide-react";

const PHONE = "0643693688";
const WHATSAPP_LINK = "https://wa.me/33643693688?text=Bonjour%2C%20je%20souhaite%20un%20devis%20pour%20une%20enseigne.";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Réalisations", href: "#realisations" },
  { label: "Tarifs", href: "#tarifs" },
  { label: "FAQ", href: "#faq" },
];

export function Navbar({ onScrollToForm }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    if (href === "#devis") { onScrollToForm(); return; }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#050505]/95 backdrop-blur-xl border-b border-[#1a1a1a] py-3"
            : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-3 h-3 rounded-full bg-[#00FF66] shadow-[0_0_10px_rgba(0,255,102,0.6)]" />
            <span className="font-['Clash_Display',sans-serif] text-xl font-bold text-white tracking-tight">
              PUB PRO
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="text-sm text-zinc-400 hover:text-white font-medium tracking-wide uppercase transition-colors duration-200 font-['Outfit',sans-serif]"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a href={`tel:${PHONE}`} className="text-zinc-400 hover:text-white text-sm flex items-center gap-1.5 transition-colors" data-testid="nav-phone">
              <Phone className="w-4 h-4" />
              06 43 69 36 88
            </a>
            <button
              onClick={onScrollToForm}
              className="bg-[#00FF66] text-black px-6 py-2.5 font-bold text-sm uppercase tracking-wider hover:shadow-[0_0_25px_rgba(0,255,102,0.4)] transition-all duration-300 font-['Outfit',sans-serif]"
              data-testid="nav-cta"
            >
              Devis gratuit &gt;
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-white p-2"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#050505]/98 backdrop-blur-xl pt-20 px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className="text-left text-lg text-white font-semibold py-4 border-b border-[#1a1a1a] font-['Outfit',sans-serif] uppercase tracking-wide"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => { setMobileOpen(false); onScrollToForm(); }}
                className="mt-6 bg-[#00FF66] text-black py-4 font-bold text-lg uppercase tracking-wider font-['Outfit',sans-serif]"
                data-testid="mobile-cta"
              >
                Devis gratuit &gt;
              </button>
              <div className="flex items-center gap-4 mt-6">
                <a href={`tel:${PHONE}`} className="flex-1 text-center py-3 border border-[#333] text-white font-semibold rounded-none">
                  Appeler
                </a>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex-1 text-center py-3 bg-[#25D366] text-white font-semibold rounded-none">
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
